import { Link } from 'react-router-dom';
import type { SolReportSourceCard } from '../../data/sol/sol-contract';
import { formatInstant } from '../../lib/timezone';
import { savedReportPath } from '../reports/saved/saved-report-route';
import { projectLabel } from './sol-wording';

/**
 * A cited SAVED REPORT, as a card that opens it. M11 / I-069.
 *
 * **A REPORT CARD, NOT A CAPTURE CARD WITH A REPORT IN IT** (I-074). Filename,
 * report date, when it was generated (in Crewlink's canonical timezone, the
 * same way the Work Log renders time — I-075), pages, and the description he
 * wrote. It links to the saved report screen, which already knows how to show
 * the PDF; nothing here reconstructs one.
 */
export interface SolReportSourceCardLinkProps {
  readonly card: SolReportSourceCard;
  readonly timeZone: string | null;
}

export function SolReportSourceCardLink({ card, timeZone }: SolReportSourceCardLinkProps) {
  const project = projectLabel(card.project.name);
  const generated = timeZone ? formatInstant(card.createdAt, timeZone) : '';
  const pages = card.pageCount === 1 ? '1 page' : `${card.pageCount} pages`;
  return (
    <Link
      className="sol-source sol-source--report"
      to={savedReportPath(card.project.id, card.source.id)}
      aria-label={`Open saved report ${card.filename} for ${project}`}
    >
      <span className="sol-source-head">
        <span className="sol-source-title">{card.filename}</span>
        <span className="sol-source-status">Report</span>
      </span>
      <span className="sol-source-meta">
        {`${project} · Report date ${card.reportDate} · ${pages}`}
        {generated ? ` · Generated ${generated}` : ''}
      </span>
      {card.description ? <span className="sol-source-caption">{card.description}</span> : null}
      <span className="sol-source-open" aria-hidden="true">
        Open report →
      </span>
    </Link>
  );
}
